import React, { useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// TS types
import { IPlanList, scrollToRef, ICommonDetailInfo } from '@redux/common';
import { RootState } from '@redux/store';

// component
import SessionBlock from './SessionBlock';
import TicketBlock from './TicketBlock';
import CalendarBlock from './CalendarBlock';
import TagsBlock from './TagsBlock';

// shared components
import Reload from '@svg/Reload';
import ResetIcon from '@svg/ResetIcon';
import LoadingIcon from '@svg/LoadingIcon';
import Loading from '@component/Common/loading';

// common function
import onSubmit from '../Common/onSubmit';
import {
    scrollToElement,
    triggerErrShake,
    THSRClearStorage,
    checkLigBoxOrDialogDisplay,
    addSeparator,
} from '@libs/core';

// plugin
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

// redux actions
import { setIsSubmitSuccess, setScrollTo, setReset } from '@redux/Normal/actions';
import AddOnPriceTag from '@component/Common/AddOnPriceTag';
import Informations from './Informations';

interface INormalDetailProps extends ICommonDetailInfo {
    // TODO: 有時間再定義
    salePriceSpread?: number;
    showCrossellTag?: boolean;
}

const DetailInfo: React.FC<INormalDetailProps> = (props) => {
    const { dialogSwitch, setDialogSwitch, isCrossell, salePriceSpread, showCrossellTag } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        NowListData,
        DetailData,
        totalAmount,
        TempCalendarData,
        isSubmitSuccess,
        isDetailLoading,
        isSessionLoading,
        calendarErrorMsg,
        sessionErrorMsg,
        ticketErrorMsg,
        Scrollto,
        submitWatcher,
    } = useSelector((state: RootState) => state.Normal);
    const { ListData } = useSelector((state: RootState) => state.Common);
    const { AdvanceTime, SaleCurrency }: IPlanList = ListData;
    const IsForSale = NowListData?.IsForSale;
    const hasCalendar = NowListData?.ExpectedUseDay;
    const hasSession = DetailData?.Sessions;
    const renderTags = useMemo(() => {
        if (DetailData) return DetailData.Tag;
    }, [DetailData]);
    const calendarBlockRef = useRef<HTMLDivElement>(null);
    const sessionBlockRef = useRef<HTMLDivElement>(null);
    const ticketBlockRef = useRef<HTMLDivElement>(null);
    // 送出前確認資料內容是否填寫正確-PC
    // TODO: 確認資料原本寫法，待迪士尼光箱上線確認沒Bug後即可刪除
    const checkData = () => {
        if (isSubmitSuccess) return;
        if (!IsForSale) return;
        // 送出前清空高鐵相關sessionStorage
        THSRClearStorage();
        // const isSuccess = onSubmit();
        // if (isSuccess) dispatch(setIsSubmitSuccess(true));
    };

    const cleanNumber = (value) => {
        if (typeof value === 'string') {
            // Remove commas from the string
            return Number(value.replace(/,/g, ''));
        }
        return Number(value);
    };
    const totalAmountCleaned = cleanNumber(totalAmount);

    useEffect(() => {
        // 清除cache btn state(從上一頁導回來因cache造成btn維持disabled與loading的狀態)
        const resetBtn = (e: any) => {
            if (e.persisted) {
                console.log('This page was restored from the bfcache.');
                dispatch(setIsSubmitSuccess(false));
            }
        };
        window.addEventListener('pageshow', resetBtn);
        return () => window.removeEventListener('pageshow', resetBtn);
    }, [dispatch]);

    useEffect(() => {
        if (!Scrollto) return;
        dispatch(setScrollTo(null));
        if (hasCalendar && Scrollto === scrollToRef.calender) {
            scrollToElement({ ref: calendarBlockRef });
        }
        if (hasSession && Scrollto === scrollToRef.session) {
            scrollToElement({ ref: sessionBlockRef });
        }
        if (Scrollto === scrollToRef.ticket) {
            scrollToElement({ ref: ticketBlockRef });
        }
    }, [dispatch, Scrollto, hasCalendar, hasSession]);

    useEffect(() => {
        if (!hasCalendar) return;
        let calendarTimer: NodeJS.Timeout | null;
        // 錨點功能
        dispatch(setScrollTo(scrollToRef.calender));
        // 月曆錯誤動畫
        if (calendarErrorMsg) {
            triggerErrShake({ ref: calendarBlockRef, timer: calendarTimer });
        }
        return () => {
            calendarTimer && clearTimeout(calendarTimer);
        };
    }, [dispatch, hasCalendar, calendarErrorMsg, submitWatcher]);

    useEffect(() => {
        if (!hasSession) return;
        let sessionTimer: NodeJS.Timeout | null;
        // 錨點功能
        dispatch(setScrollTo(scrollToRef.session));
        // 場次錯誤動畫
        if (sessionErrorMsg) {
            triggerErrShake({ ref: sessionBlockRef, timer: sessionTimer });
        }
        return () => {
            sessionTimer && clearTimeout(sessionTimer);
        };
    }, [dispatch, hasSession, sessionErrorMsg, submitWatcher]);

    useEffect(() => {
        let ticketTimer: NodeJS.Timeout | null;
        // 錨點功能
        dispatch(setScrollTo(scrollToRef.ticket));
        // 票種錯誤動畫
        if (ticketErrorMsg) {
            triggerErrShake({ ref: ticketBlockRef, timer: ticketTimer });
        }
        return () => {
            ticketTimer && clearTimeout(ticketTimer);
        };
    }, [dispatch, ticketErrorMsg, submitWatcher]);

    return (
        DetailData && (
            <div className="detailInfo">
                {!!DetailData.Tag && DetailData.Tag.length > 0 && <TagsBlock data={renderTags} />}
                <div className="selectedBlock">
                    <div className="resetBlock">
                        <div
                            onClick={() => {
                                dispatch(setReset());
                                /* TODO: 第二階段待處理
                                hasSession &&
                                setGDSSessionData({
                                    dispatch,
                                    date: GDetail?.EarliestBuyDay,
                                    FormatID: GNowListData?.FormatID,
                                    ETID,
                                    context: {
                                        setGSessionLoading: setIsGDSSessionLoading,
                                        setGSessionFetchFail: setGDSSessionFetchFail,
                                        setSession,
                                        setSessionPicked,
                                    },
                                });
                                */
                            }}
                        >
                            <ResetIcon color="#000000" />
                            <span>{t('noTHSR.detailInfo.allReset')}</span>
                        </div>
                    </div>
                    <div className="selectContainer">
                        {hasCalendar && TempCalendarData && (
                            <div className="calendarBlock" ref={calendarBlockRef}>
                                <div className="calendarTitle">
                                    <div className="subTitle">
                                        {t('noTHSR.detailInfo.choose_date_departure')}
                                    </div>
                                    <div
                                        className={cx('errorMsg', {
                                            showError: !!calendarErrorMsg,
                                        })}
                                    >
                                        {calendarErrorMsg}
                                    </div>
                                </div>
                                <div className="calendarWrap">
                                    <CalendarBlock />
                                    {isSessionLoading && (
                                        <div className="loadingCalendar">
                                            <Loading icon={<LoadingIcon />} />
                                        </div>
                                    )}
                                </div>
                                <div className="calendarNotice">
                                    {t('noTHSR.detailInfo.displayedInCurrency', {
                                        SaleCurrency: SaleCurrency,
                                    })}
                                </div>
                            </div>
                        )}
                        <div className="detailBlock">
                            {/* TODO: 第二階段待處理 */}
                            {/*{GNowListData?.ExpectedUseDay && GDetail.Sessions && (
                                <div className="sessionBlock" ref={sessionBlockRef}>
                                    <div className="sessionTitle">
                                        <div className="subTitle">場次/時間</div>
                                        <div
                                            className={cx('errorMsg', {
                                                showError: !!GSessionErr,
                                            })}
                                        >
                                            {GSessionErr}
                                        </div>
                                    </div>
                                    {isGDSSessionLoading ? (
                                        <div className="loadBlock">
                                            // { <Loading icon={<LoadingIcon />} text={'場次載入中'} /> }
                                        </div>
                                    ) : GDSSessionFetchFail ? (
                                        <div>取得場次失敗，請改選日期</div>
                                    ) : (
                                        <SessionBlock />
                                    )}
                                </div>
                            )}*/}
                            {/* 無場次 月份切換loading */}
                            {/* {GNowListData?.ExpectedUseDay &&
                                !GDetail.Sessions &&
                                (isGDSSessionLoading ? (
                                    <>
                                        <div className="ticketBlock">
                                            <div className="ticketTitleWrap">
                                                <div className="subTitle">票種/數量</div>
                                            </div>
                                        </div>
                                        <div className="sessionBlock">
                                            <div className="loadBlock">
                                                // <Loading icon={<LoadingIcon />} text={'載入中'} />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div></div>
                                ))
                            } */}
                            {hasSession && <SessionBlock ref={sessionBlockRef} />}

                            <TicketBlock ref={ticketBlockRef} isCrossell={isCrossell} />

                            <div className={cx('priceArea', { load_hide: isSessionLoading })}>
                                <div className="totalPrice">
                                    {isCrossell && showCrossellTag && (
                                        <div className="addOnPrice">
                                            <AddOnPriceTag />
                                        </div>
                                    )}
                                    <span className="totalPriceTxt">
                                        {t('noTHSR.detailInfo.totalPrice')}
                                    </span>
                                    <span className="saleCurrency ml-5">{SaleCurrency}</span>
                                    <span className="totalAmount">
                                        {addSeparator(totalAmountCleaned)}
                                    </span>
                                    {isCrossell &&
                                        totalAmountCleaned > 0 &&
                                        salePriceSpread > 0 && (
                                            <>
                                                <div className="originalPrice">
                                                    <span>{SaleCurrency}</span>
                                                    <span>{addSeparator(totalAmountCleaned + salePriceSpread)}</span>
                                                </div>
                                                <div className="priceSavings">
                                                    <span>省下</span>
                                                    <span>-</span>
                                                    <span>${addSeparator(salePriceSpread)}</span>
                                                </div>
                                            </>
                                        )}
                                </div>
                            </div>
                            <div className={cx('submitBlock', { load_hide: isSessionLoading })}>
                                <div
                                    className={cx('submitBtn', {
                                        disabled: isSubmitSuccess,
                                        notReady: AdvanceTime.length > 0,
                                        allsold: !IsForSale,
                                    })}
                                    onClick={async () => {
                                        try {
                                            if (isSubmitSuccess) return;
                                            if (!IsForSale) return;
                                            // 送出前清空高鐵相關sessionStorage
                                            THSRClearStorage();
                                            const isSuccess = await onSubmit('checkData');
                                            if (typeof isSuccess === 'undefined') return;
                                            if (isSuccess.selectChecked) {
                                                // 確認是否為迪士尼產品
                                                if (
                                                    checkLigBoxOrDialogDisplay(
                                                        dialogSwitch,
                                                        setDialogSwitch,
                                                        'disney'
                                                    )
                                                ) {
                                                    if (!isSubmitSuccess) {
                                                        setDialogSwitch((prev) => {
                                                            prev.disney = true;
                                                            return { ...prev };
                                                        });
                                                    }
                                                } else {
                                                    dispatch(setIsSubmitSuccess(true));
                                                    onSubmit('redirect');
                                                }
                                            } else if (
                                                !isSuccess.selectChecked &&
                                                'IsSuccess' in isSuccess.apiChecked &&
                                                !isSuccess.apiChecked.IsSuccess
                                            ) {
                                                // 驗價錯誤則顯示提示重整頁面光箱
                                                setDialogSwitch((prev) => {
                                                    prev.verify = true;
                                                    return { ...prev };
                                                });
                                            }
                                        } catch (e) {
                                            throw e;
                                        }
                                    }}
                                >
                                    <div>
                                        {AdvanceTime.length > 0
                                            ? t('noTHSR.detailInfo.comingSoon')
                                            : !IsForSale
                                            ? t('noTHSR.detailInfo.restocking')
                                            : t('noTHSR.detailInfo.bookNow')}
                                    </div>
                                    {isSubmitSuccess && (
                                        <div className="reload">
                                            <Reload color="#ffffff" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Informations formatId={NowListData?.FormatID}/>
            </div>
        )
    );
};

export default DetailInfo;
